import * as React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';

import * as api from '../../../api';
import * as FormUtils from '../../../utils/form';
import * as rootReducer from '../../../reducers/root';
import * as store from '../../../reducers/store';
import * as Navigation from '../../../utils/navigation';

import { Button } from '../../../components/Button';
import { InfoMessage } from '../../../components/InfoMessage';
import { Question, StaticStatus } from '../../../components/Question';

import { sendEventMessage, Message } from '../../../utils/native';
import { getSelectedForms } from '../../../utils/formSet';

import defaultStyles from '../../../styles/formSetStyles.module.css';
import {
  getMaybeFormSetSelectedTimeSlot,
  getMaybeSelectedEhrProviderWithPractice,
} from '../../../utils/scheduling';
import { compileLabelFromFieldOrPatient } from '../../../components/utils';
import { getProviderNameFull } from '../../../utils/provider';
import { getUseAlternateEnum } from '../../../utils/form/condition';

///////////////////////////////////////////////////
//////TYPES

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

interface StateProps
  extends Pick<store.Inbound, 'selectedProviderId' | 'ehrProviderInformation'> {
  forms: store.Forms;
  formSets: store.FormSets;
  patient: api.Patient.Patient | null;
  selectedFormSetId: string;
}

interface DispatchProps {
  setFormField: (formFieldId: string, value: string) => void;
}

interface Props extends DispatchProps, StateProps, WithTranslation {
  page: number;
  formFields: store.FormFieldWithConfig[];
  formSetId: string;
  navigateToNextPage: () => Promise<void>;
  practicePreference: api.Previsit.PracticePreference;
  handleLastPage: (formSetId: string, formFieldId: string) => Promise<void>;
}

interface State {
  isLoading: boolean;
  errorMessage?: string;
}

///////////////////////////////////////////////////
//////COMPONENT

class PageComponent extends React.Component<Props, State> {
  state: State = {
    isLoading: false,
  };

  componentDidMount() {
    this.evaluateDisplayableFields();
  }

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
    snapshot?: any,
  ): void {
    if (prevProps.page !== this.props.page) {
      this.evaluateDisplayableFields();
      this.setState({
        errorMessage: undefined,
      });
    } else if (prevProps.formFields !== this.props.formFields) {
      this.evaluateDisplayableFields();
    }
  }

  updateHiddenFieldsValues = async (
    hiddenFields: store.FormFieldWithConfig[],
  ) => {
    const toUpdate = hiddenFields.filter((field) => field.value);
    if (toUpdate.length) {
      toUpdate.forEach((field) => this.props.setFormField(field.id, ''));
      await Promise.all(
        toUpdate.map((field) => this.saveFieldValue(field.id, '', false)),
      );
    }
  };

  evaluateDisplayableFields = () => {
    const { page, formFields, patient } = this.props;
    const pageFormFields = Navigation.getPageFormFields(page, formFields);
    const hiddenFields = pageFormFields.filter((field) =>
      FormUtils.checkIsHidden(field, formFields, patient),
    );
    this.updateHiddenFieldsValues(hiddenFields);
  };

  saveFieldValue = async (
    formFieldId: string,
    value: string,
    activeAction = true,
  ): Promise<void> => {
    const { formSetId, forms } = this.props;
    const field = _.flatten(
      Object.values(forms).map((form) => form.fields),
    ).find((field) => field.id === formFieldId);
    await api.client.Previsit.updateFormField(formSetId, formFieldId, value);
    if (!field) {
      console.warn('field not found!');
      return;
    }
    if (activeAction) sendEventMessage(Message.ActiveAction);
  };

  markStaticAsSubmitted = (
    toUpdate: store.FormFieldWithConfig[],
  ): store.FormFieldWithConfig[] => {
    return toUpdate.map((formField) => {
      if (
        formField.value === StaticStatus.Viewed &&
        formField.primitive === Primitive.Static
      ) {
        return { ...formField, value: StaticStatus.Submitted };
      }
      return formField;
    });
  };

  savePageValues = async (
    toUpdate: store.FormFieldWithConfig[],
  ): Promise<void> => {
    const { formSetId } = this.props;
    const updated = this.markStaticAsSubmitted(toUpdate);
    await api.client.Previsit.updateFormFields(formSetId, updated);
  };

  getMaybeLabelExtraParameters = ():
    | Record<string, string | null>
    | undefined => {
    const {
      page,
      formFields,
      patient,
      selectedProviderId,
      ehrProviderInformation,
    } = this.props;
    const selectedTimeSlot = getMaybeFormSetSelectedTimeSlot(
      formFields,
      page,
      patient ?? undefined,
    );
    const selectedProvider = getMaybeSelectedEhrProviderWithPractice(
      ehrProviderInformation,
      selectedTimeSlot,
      selectedProviderId,
    );
    return selectedProvider
      ? {
          providerFullName:
            getProviderNameFull({ provider: selectedProvider }) || null,
          providerFirstName: selectedProvider.firstName,
          providerLastName: selectedProvider.lastName,
          providerPhoneNumber: selectedProvider.phoneNumber,
          practicePhoneNumber: selectedProvider.practicePhoneNumber,
          practiceName: selectedProvider.name,
        }
      : undefined;
  };

  navigateToNextPage = async () => {
    const {
      page,
      formFields,
      navigateToNextPage,
      patient,
      practicePreference,
    } = this.props;
    const { isLoading } = this.state;
    if (isLoading) return;
    this.setState({ isLoading: true });
    const pageFormFields = Navigation.getPageFormFields(page, formFields);
    const extraParameters = this.getMaybeLabelExtraParameters();
    try {
      FormUtils.validatePageValues(
        pageFormFields.map((f) => ({
          ...f,
          label: compileLabelFromFieldOrPatient({
            label: f.label,
            page: f.page,
            patient,
            rank: f.rank,
            extraParameters,
            fields: formFields,
          }),
        })),
        formFields,
        patient,
        practicePreference,
      );
    } catch (err) {
      this.setState({
        errorMessage: err.message,
      });
      window.scrollTo(0, 0);
      this.setState({ isLoading: false });
      return;
    }

    // Pages with no questions show only a "continue" button
    if (pageFormFields.length) {
      await this.savePageValues(pageFormFields);
    }
    await navigateToNextPage();
    sendEventMessage(Message.ActiveAction);
    this.setState({ isLoading: false });
  };

  hideContinue = (
    displayableFormFields: api.PrevisitFormField.PrevisitFormFieldWithConfig[],
    useAlternateEnum: boolean,
  ) => {
    const isOnlyScheduleLinkFormField = displayableFormFields.some(
      (formField) => [Primitive.ScheduleLink].includes(formField.primitive),
    );
    const isLastPage = !!displayableFormFields.find((f) => f.lastPage);
    const hideContinueButton =
      useAlternateEnum ||
      isOnlyScheduleLinkFormField ||
      isLastPage ||
      !!displayableFormFields.find((f) => f.hideContinueButton);
    return hideContinueButton;
  };

  render() {
    const {
      practicePreference,
      patient,
      formFields,
      setFormField,
      t,
      selectedFormSetId,
      formSets,
      page,
    } = this.props;
    const pageFormFields = Navigation.getPageFormFields(page, formFields);
    const displayableFormFields = pageFormFields.filter(
      (field) => !FormUtils.checkIsHidden(field, formFields, patient),
    );
    const { isLoading, errorMessage } = this.state;

    /**
     * Rules to use alternate enum:
     * 1. only single question on page (including conditional questions)
     * 2. that 1 question is of primitive = ENUM
     * 3. that enum question has 5 or less options
     */
    const useAlternateEnum = getUseAlternateEnum(
      pageFormFields,
      displayableFormFields,
    );

    return (
      <div>
        {errorMessage ? (
          <InfoMessage isError={true}>
            {errorMessage
              ?.split(/"*"/)
              .map((s) => t(s))
              .join('"') || ''}
          </InfoMessage>
        ) : null}
        <form>
          {displayableFormFields.map((field) => (
            <Question
              practicePreference={practicePreference}
              key={field.id}
              field={field}
              useAlternateEnum={useAlternateEnum}
              saveFieldValue={async (value: string) =>
                this.saveFieldValue(field.id, value)
              }
              updateFieldValue={(value: string) =>
                setFormField(field.id, value)
              }
              navigateToNextPage={this.navigateToNextPage}
              fields={formFields}
              patient={patient}
              selectedFormSetId={selectedFormSetId}
              formSets={formSets}
              handleLastPage={this.props.handleLastPage}
              disabled={isLoading}
            />
          ))}
        </form>
        {this.hideContinue(displayableFormFields, useAlternateEnum) ? null : (
          <div className={defaultStyles.ButtonSubmitWrapper}>
            <Button
              type="button"
              children={isLoading ? t('Loading...') : t('Continue')}
              disabled={isLoading}
              onClick={this.navigateToNextPage}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  forms: getSelectedForms(store.formSets, store.selectedFormSetId),
  selectedFormSetId: store.selectedFormSetId,
  patient: store.loggedInPatient,
  formSets: store.formSets,
  ehrProviderInformation: store.inbound.ehrProviderInformation,
  selectedProviderId: store.inbound.selectedProviderId,
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormField: (formFieldId: string, value: string) =>
      dispatch(rootReducer.setFormField({ formFieldId, value })),
  };
}

export const Page = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(PageComponent));
